/**
 * FacetsPanel — left faceted-search column on the Base screen (258–560px; ширину
 * задаёт App через usePanelWidth, панель тянется на всю доступную ширину).
 * Replaces the FilterBuilder in the Base flow (FilterBuilder is kept for BulkActions).
 *
 * Each group renders option checkboxes with a live count (useFacetCount). Selecting
 * options inside a group builds ONE FilterClause for that group's field
 * ({field, operator:'among', value:[...]}, or {operator:'eq', value} for a single
 * pick); groups are merged and written wholesale via useFilters().setFilters.
 *
 * Options come from resolveOptions(FIELD_BY_KEY[field], refs) — no parsing here.
 * Styling per mockup_2.dc.html (facets §) and README §Design Tokens.
 */

import { useMemo, useState } from "react";

import type { FilterClause, Refs } from "@/api/client";
import { useFacetCount, useRefs } from "@/api/hooks";
import { FIELD_BY_KEY, facetValuesClause, resolveOptions, type FieldOption } from "@/lib/fields";
import { nextFacetValues } from "@/lib/facets";
import { useFilters } from "@/store/filters";
import { FacetCheckbox } from "@/components/FacetCheckbox";
import { CellTreeSelect } from "@/components/CellTreeSelect";

interface FacetGroup {
  title: string;
  /** Upstream field key (also the FilterClause.field). */
  field: string;
  /** Options sourced from refs via resolveOptions(FIELD_BY_KEY[field]). */
  refsKey?: string;
  /** Fixed string options (used for readiness, which is not in /api/refs). */
  staticOptions?: string[];
  /** Show a substring search input above the options (Отделение). */
  searchable?: boolean;
  /** Collapse the option list to the first few with a "Показать ещё N" toggle. */
  showMore?: boolean;
  /** Render collapsed (header only) by default. */
  collapsedByDefault?: boolean;
  /**
   * Radio-mode: picking a value REPLACES any previously selected value in this
   * group (repeat click on the selected value clears it) and the resulting
   * clause is always `{field, operator: "eq", value}` — never `among`. Required
   * for fields where mob-api rejects `__among` (e.g. cfabzb → HTTP 400).
   */
  single?: boolean;
}

// collapsedByDefault is set on every "heavy/multiple-value" group so the FIRST render
// of the Base screen fires only a handful of /api/volunteers/count requests (one per
// VISIBLE value of the few EXPANDED groups), not dozens. Counts for a group are fetched
// lazily — only when the user opens it (see FacetOption enabled gating). We keep just
// "Готовность (регион)" open by default (3 values → 3 counts on load); branch_id (Отделение)
// is the heaviest list, so it stays collapsed.
const GROUPS: FacetGroup[] = [
  {
    title: "Отделение",
    field: "branch_id",
    refsKey: "branches",
    searchable: true,
    showMore: true,
    collapsedByDefault: true,
  },
  { title: "Готовность (регион)", field: "readiness_region", staticOptions: ["Да", "Нет", "На проверке"] },
  {
    title: "Участие ЕДГ-2026 (явка онлайн)",
    field: "cfabzb",
    staticOptions: ["Рег онлайн", "Нет", "QR-код", "Гео-СМС"],
    single: true,
    collapsedByDefault: true,
  },
  { title: "Готовность", field: "readiness", staticOptions: ["Да", "Нет"], collapsedByDefault: true },
  { title: "Лояльность", field: "loyalty", refsKey: "loyalty", collapsedByDefault: true },
  { title: "Членство", field: "membership", refsKey: "membership", collapsedByDefault: true },
  {
    title: "Целевые группы",
    field: "target_group_ids",
    refsKey: "targetGroups",
    collapsedByDefault: true,
  },
];

/** First N options shown when showMore is on. */
const SHOW_MORE_LIMIT = 3;
/** How many more options each "Показать ещё" click reveals (bounds concurrent /count). */
const REVEAL_STEP = 25;

/** Extract the selected values for `field` from the applied clause list. */
function selectedFor(filters: FilterClause[], field: string): Set<string> {
  const clause = filters.find((c) => c.field === field);
  if (!clause) return new Set();
  const v = clause.value;
  if (Array.isArray(v)) return new Set(v.map(String));
  if (v == null) return new Set();
  return new Set([String(v)]);
}

/** All applied clauses EXCEPT this group's field (= base for that group's counts). */
function baseFilters(filters: FilterClause[], field: string): FilterClause[] {
  return filters.filter((c) => c.field !== field);
}

export function FacetsPanel() {
  const { data: refs } = useRefs();
  const filters = useFilters((s) => s.filters);
  const setFilters = useFilters((s) => s.setFilters);
  const clearFilters = useFilters((s) => s.clearFilters);

  // Per-group collapsed state (seed from collapsedByDefault).
  const [collapsed, setCollapsed] = useState<Record<string, boolean>>(() =>
    Object.fromEntries(GROUPS.map((g) => [g.field, !!g.collapsedByDefault])),
  );

  const toggleGroup = (field: string) =>
    setCollapsed((s) => ({ ...s, [field]: !s[field] }));

  /** Toggle one option value inside a group → rebuild the merged clause list. */
  const toggleValue = (field: string, value: string) => {
    const group = GROUPS.find((g) => g.field === field);
    const single = !!group?.single;
    const current = selectedFor(filters, field);
    const values = nextFacetValues(current, value, single);

    const others = filters.filter((c) => c.field !== field);
    if (values.length === 0) {
      setFilters(others);
      return;
    }
    if (single) {
      // Radio-mode groups (cfabzb) must always clause as `eq` with the one picked
      // value — mob-api rejects `cfabzb__among` with HTTP 400 — regardless of what
      // facetValuesClause would default to for the field's kind.
      setFilters([...others, { field, operator: "eq", value: values[0] }]);
      return;
    }
    // Operator is chosen from the field kind: multiselect array fields need `among`
    // even for a single value; `eq` is invalid for them upstream (F22).
    setFilters([...others, facetValuesClause(field, values)]);
  };

  return (
    <div style={{ display: "flex", flexDirection: "column" }}>
      {/* header */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          padding: "14px 16px 10px",
        }}
      >
        <span
          style={{
            fontSize: 12,
            fontWeight: 700,
            letterSpacing: ".06em",
            textTransform: "uppercase",
            color: "var(--text-2, #475569)",
          }}
        >
          Фильтры
        </span>
        <span
          role="button"
          tabIndex={0}
          onClick={() => clearFilters()}
          onKeyDown={(e) => {
            if (e.key === " " || e.key === "Enter") {
              e.preventDefault();
              clearFilters();
            }
          }}
          className="rounded-[4px] outline-none focus-visible:ring-2 focus-visible:ring-[var(--accent-hex)]"
          style={{ fontSize: 12, color: "var(--accent-hex, #2563EB)", cursor: "pointer" }}
        >
          Сбросить
        </span>
      </div>

      {GROUPS.map((g) => (
        <FacetGroupView
          key={g.field}
          group={g}
          refs={refs}
          filters={filters}
          collapsed={!!collapsed[g.field]}
          onToggleGroup={() => toggleGroup(g.field)}
          onToggleValue={(value) => toggleValue(g.field, value)}
        />
      ))}

      <CellFacetSection filters={filters} setFilters={setFilters} />
    </div>
  );
}

/**
 * "Ячейка" facet — a single-cell picker reusing the lazy <CellTreeSelect> (expand
 * a parent/root, then pick any node incl. a child). Unlike the checkbox groups above
 * it renders the tree rather than counted options, and builds ONE origin_cell_id
 * clause. The "включая дочерние" checkbox toggles the operator: child_of (cell + all
 * descendants) vs eq (exactly the picked cell) — matching origin_cell_id in fields.ts.
 */
function CellFacetSection({
  filters,
  setFilters,
}: {
  filters: FilterClause[];
  setFilters: (filters: FilterClause[]) => void;
}) {
  const clause = filters.find((c) => c.field === "origin_cell_id");
  const value = clause ? [Number(clause.value)] : [];
  const includeChildren = clause?.operator === "child_of";

  // Collapsed by default (opening the tree lazily loads the cell roots) — but start
  // open when a cell is already applied so the current pick is visible.
  const [collapsed, setCollapsed] = useState(!clause);

  return (
    <div style={{ padding: "10px 16px 12px", borderBottom: "1px solid var(--divider, #EBF0F6)" }}>
      <div
        onClick={() => setCollapsed((c) => !c)}
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          fontSize: 12.5,
          fontWeight: 600,
          color: "var(--text-1, #334155)",
          cursor: "pointer",
          marginBottom: collapsed ? 0 : 8,
        }}
      >
        Ячейка
        <span style={{ color: "var(--text-muted, #94A3B8)" }}>{collapsed ? "▸" : "▾"}</span>
      </div>

      {!collapsed && (
        <CellTreeSelect
          mode="single"
          value={value}
          includeChildren={includeChildren}
          onChange={(ids, inc) => {
            const others = filters.filter((c) => c.field !== "origin_cell_id");
            if (ids.length === 0) {
              setFilters(others);
              return;
            }
            setFilters([
              ...others,
              {
                field: "origin_cell_id",
                operator: inc ? "child_of" : "eq",
                value: ids[0],
              },
            ]);
          }}
        />
      )}
    </div>
  );
}

interface FacetGroupViewProps {
  group: FacetGroup;
  refs: Refs | undefined;
  filters: FilterClause[];
  collapsed: boolean;
  onToggleGroup: () => void;
  onToggleValue: (value: string) => void;
}

function FacetGroupView({
  group,
  refs,
  filters,
  collapsed,
  onToggleGroup,
  onToggleValue,
}: FacetGroupViewProps) {
  const [search, setSearch] = useState("");
  // How many options are currently revealed (each fires its own /count). Grows by
  // REVEAL_STEP per "Показать ещё" click instead of revealing ALL at once, so a big
  // list (branch_id) never fires hundreds of /count requests through the 2 rps
  // throttle in one burst (F25).
  const [revealed, setRevealed] = useState(SHOW_MORE_LIMIT);

  const selected = selectedFor(filters, group.field);
  const base = useMemo(() => baseFilters(filters, group.field), [filters, group.field]);

  // Resolve options: static strings, or via refs (no local parsing).
  const allOptions: FieldOption[] = useMemo(() => {
    if (group.staticOptions) return group.staticOptions.map((s) => ({ value: s, label: s }));
    const def = FIELD_BY_KEY[group.field];
    return def ? resolveOptions(def, refs) : [];
  }, [group, refs]);

  const filteredOptions = useMemo(() => {
    if (!group.searchable || !search.trim()) return allOptions;
    const q = search.trim().toLowerCase();
    return allOptions.filter((o) => o.label.toLowerCase().includes(q));
  }, [allOptions, group.searchable, search]);

  // showMore groups reveal a bounded window; a search still caps to REVEAL_STEP so a
  // broad query can't fire a count per match. Non-showMore groups (small static lists)
  // show everything.
  const cap = search.trim() ? Math.max(revealed, REVEAL_STEP) : revealed;
  const visibleOptions = group.showMore ? filteredOptions.slice(0, cap) : filteredOptions;
  const hiddenCount = filteredOptions.length - visibleOptions.length;

  return (
    <div style={{ padding: "10px 16px 12px", borderBottom: "1px solid var(--divider, #EBF0F6)" }}>
      {/* group header (collapsible) */}
      <div
        role="button"
        tabIndex={0}
        aria-expanded={!collapsed}
        onClick={onToggleGroup}
        onKeyDown={(e) => {
          if (e.key === " " || e.key === "Enter") {
            e.preventDefault();
            onToggleGroup();
          }
        }}
        className="rounded-[4px] outline-none focus-visible:ring-2 focus-visible:ring-[var(--accent-hex)]"
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          fontSize: 12.5,
          fontWeight: 600,
          color: "var(--text-1, #334155)",
          cursor: "pointer",
          marginBottom: collapsed ? 0 : 8,
        }}
      >
        {group.title}
        <span style={{ color: "var(--text-muted, #94A3B8)" }}>{collapsed ? "▸" : "▾"}</span>
      </div>

      {!collapsed && (
        <>
          {group.searchable && (
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: 7,
                background: "var(--panel, #fff)",
                border: "1px solid var(--border-hex, #E2E8F0)",
                borderRadius: 7,
                padding: "5px 9px",
                marginBottom: 8,
              }}
            >
              <span
                style={{
                  width: 11,
                  height: 11,
                  borderRadius: "50%",
                  border: "1.4px solid var(--control-border-2, #B6C2D2)",
                  flexShrink: 0,
                }}
              />
              <input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="найти отделение…"
                style={{
                  flex: 1,
                  minWidth: 0,
                  border: "none",
                  outline: "none",
                  background: "transparent",
                  fontSize: 12,
                  color: "var(--text-1, #334155)",
                }}
              />
            </div>
          )}

          {visibleOptions.map((opt) => (
            <FacetOption
              key={opt.value}
              field={group.field}
              option={opt}
              base={base}
              checked={selected.has(opt.value)}
              onToggle={onToggleValue}
              // Counts are fetched ONLY for an expanded group's currently-visible values.
              // Collapsed groups don't mount FacetOption at all; values hidden behind
              // "Показать ещё N" aren't in visibleOptions — so neither fires a count.
              enabled={!collapsed}
            />
          ))}

          {group.showMore && hiddenCount > 0 && (
            <div
              role="button"
              tabIndex={0}
              onClick={() => setRevealed((r) => r + REVEAL_STEP)}
              onKeyDown={(e) => {
                if (e.key === " " || e.key === "Enter") {
                  e.preventDefault();
                  setRevealed((r) => r + REVEAL_STEP);
                }
              }}
              className="rounded-[4px] outline-none focus-visible:ring-2 focus-visible:ring-[var(--accent-hex)]"
              style={{ fontSize: 12, color: "var(--accent-hex, #2563EB)", cursor: "pointer", paddingTop: 5 }}
            >
              Показать ещё {Math.min(hiddenCount, REVEAL_STEP)} ▾
            </div>
          )}
        </>
      )}
    </div>
  );
}

interface FacetOptionProps {
  field: string;
  option: FieldOption;
  base: FilterClause[];
  checked: boolean;
  onToggle: (value: string) => void;
  /** When false, the count query is NOT issued (lazy facets). */
  enabled: boolean;
}

/**
 * One option row — owns its count query so a group only fetches visible options, and
 * only while the group is expanded (enabled). The checkbox stays fully interactive
 * regardless of whether/when the count has loaded.
 */
function FacetOption({ field, option, base, checked, onToggle, enabled }: FacetOptionProps) {
  const { data } = useFacetCount(base, field, option.value, enabled);
  return (
    <FacetCheckbox
      label={option.label}
      value={option.value}
      count={data ? data.count : null}
      checked={checked}
      onToggle={onToggle}
    />
  );
}
